import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { ShoppingBag, Plus, Minus, Check } from "lucide-react";
import { usePageSEO, SEO } from "@/lib/seo";

/** DA is default. */
const useT = () => {
  const { lang } = useI18n();
  return (da, en, es) => ({ da, en, es })[lang] || da;
};

export default function BuyShirt() {
  const { lang } = useI18n();
  const t = useT();
  usePageSEO(SEO.shirts.title, SEO.shirts.description);
  const [config, setConfig] = useState(null);
  const [counts, setCounts] = useState({}); // size -> qty
  const [form, setForm] = useState({ full_name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/shirts/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const inc = (size) => setCounts((c) => ({ ...c, [size]: (c[size] || 0) + 1 }));
  const dec = (size) => setCounts((c) => ({ ...c, [size]: Math.max(0, (c[size] || 0) - 1) }));

  const totalQty = useMemo(
    () => Object.values(counts).reduce((a, b) => a + Number(b || 0), 0),
    [counts]
  );
  const totalDkk = (config?.unit_price_dkk || 0) * totalQty;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (totalQty < 1) {
      setErr(t("Vælg mindst én T-shirt.", "Select at least one shirt.", "Selecciona al menos una camiseta."));
      return;
    }
    const shirt_sizes = [];
    Object.entries(counts).forEach(([size, q]) => { for (let i = 0; i < Number(q || 0); i++) shirt_sizes.push(size); });
    setBusy(true);
    try {
      const { data } = await api.post("/shirts/checkout", {
        ...form,
        shirt_sizes,
        origin_url: window.location.origin,
        language: lang,
      });
      window.location.href = data.checkout_url;
    } catch (ex) {
      setErr(ex.response?.data?.detail || t("Der opstod en fejl.", "Something went wrong.", "Ha ocurrido un error."));
      setBusy(false);
    }
  };

  return (
    <div className="max-editorial container-x py-20 max-w-4xl">
      <div className="label-mono text-terracotta">
        {t("DANAMARK T-SHIRT", "DANAMARK T-SHIRT", "CAMISETA DANAMARK")}
      </div>
      <h1 data-testid="buy-shirt-title" className="font-editorial text-5xl md:text-6xl mt-3 tracking-tighter">
        {t("Køb T-shirt", "Buy a T-shirt", "Compra una camiseta")}
      </h1>
      <p className="mt-6 text-ink/70 text-lg md:text-xl max-w-2xl leading-relaxed">
        {t(
          "DANAmark T-shirten koster 150 DKK. Køb en eller flere. Hvert køb bidrager direkte til vores donationsmål.",
          "The DANAmark T-shirt costs 150 DKK. Buy one or several. Every purchase contributes directly towards our donation goal.",
          "La camiseta DANAmark cuesta 150 DKK. Compra una o varias. Cada compra contribuye directamente a nuestra meta de donación."
        )}
      </p>
      <div className="mt-4 inline-flex items-center gap-2 text-sm text-ink/60 bg-terracotta/10 text-terracotta px-4 py-2 rounded-full">
        <ShoppingBag size={16} />
        {t(
          "Forsalg · afhentes under løbet · ingen postforsendelse.",
          "Pre-sale · pickup during the race · no shipping.",
          "Preventa · recogida durante la carrera · sin envío postal."
        )}
      </div>

      <form onSubmit={submit} className="mt-12 space-y-10" data-testid="buy-shirt-form">
        {/* Size picker */}
        <section>
          <div className="label-mono text-ink/60">{t("Vælg størrelser", "Choose sizes", "Elige tallas")}</div>

          {config && (
            <div className="mt-4">
              <SizeGroup title={t("Størrelse", "Size", "Talla")} sizes={config.sizes_adult} counts={counts} inc={inc} dec={dec} />
            </div>
          )}
        </section>

        {/* Contact */}
        <section className="space-y-6">
          <Field label={t("Fulde navn", "Full name", "Nombre completo")} testid="bs-name"
                 v={form.full_name} onChange={(v) => setForm((f) => ({ ...f, full_name: v }))} required />
          <Field label={t("E-mail", "Email", "Correo electrónico")} testid="bs-email" type="email"
                 v={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} required />
          <Field label={t("Telefon", "Phone", "Teléfono")} testid="bs-phone"
                 v={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} required />
        </section>

        {err && <div className="text-sm text-red-600" data-testid="bs-error">{err}</div>}

        <div className="flex items-center justify-between border-t border-line pt-6 flex-wrap gap-4">
          <div>
            <div className="label-mono text-ink/50">
              {t("Antal", "Quantity", "Cantidad")}: <span data-testid="bs-qty">{totalQty}</span>
            </div>
            <div className="font-editorial text-3xl text-terracotta" data-testid="bs-total">{totalDkk} kr</div>
          </div>
          <button type="submit" disabled={busy || totalQty < 1} data-testid="bs-submit"
                  className="bg-terracotta hover:bg-terracotta-dark disabled:opacity-60 text-white px-8 py-4 text-sm tracking-wide rounded-full inline-flex items-center gap-2">
            {busy ? "…" : (
              <>
                {t("Til betaling", "Continue to payment", "Continuar al pago")} →
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function SizeGroup({ title, sizes, counts, inc, dec }) {
  return (
    <div>
      <div className="label-mono text-ink/50 mb-3">{title}</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {sizes.map((s) => (
          <div key={s} data-testid={`bs-size-${s}`}
               className="border border-line rounded-lg p-4 flex items-center justify-between">
            <span className="font-editorial text-lg tabular-nums">{s}</span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => dec(s)}
                      data-testid={`bs-dec-${s}`}
                      className="w-8 h-8 rounded-full border border-line hover:border-ink flex items-center justify-center">
                <Minus size={14} />
              </button>
              <span data-testid={`bs-count-${s}`} className="w-6 text-center tabular-nums">{counts[s] || 0}</span>
              <button type="button" onClick={() => inc(s)}
                      data-testid={`bs-inc-${s}`}
                      className="w-8 h-8 rounded-full bg-ink text-white hover:bg-terracotta flex items-center justify-center">
                <Plus size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, v, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="label-mono text-ink/60">{label}</label>
      <input data-testid={testid} type={type} required={required} value={v}
             onChange={(e) => onChange(e.target.value)}
             className="mt-2 w-full border border-line focus:border-terracotta outline-none px-4 py-3 bg-transparent" />
    </div>
  );
}

export function BuyShirtThankYou() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const t = useT();
  const [status, setStatus] = useState("loading");
  const [rec, setRec] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        setRec(r.data);
        if (r.data.payment_status === "paid") { setStatus("paid"); return; }
      } catch (err) { console.warn("Poll failed", err); }
      if (attempts < 20) setTimeout(poll, 2000); else setStatus("timeout");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-editorial container-x py-24 text-center max-w-2xl">
      {status === "paid" ? (
        <>
          <div className="mx-auto w-14 h-14 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center">
            <Check size={28} />
          </div>
          <div className="label-mono text-terracotta mt-6">
            {t("Bestilling bekræftet", "Order confirmed", "Pedido confirmado")}
          </div>
          <h1 data-testid="bs-thanks-title" className="font-editorial text-5xl md:text-6xl mt-3 tracking-tighter">
            {t("Tak — vi ses på løbedagen.", "Thank you — see you on race day.", "Gracias — nos vemos el día de la carrera.")}
          </h1>
          <p className="mt-6 text-ink/70">
            {t(
              "Din T-shirt afhentes under Social Run for Spain. Der er ingen postforsendelse.",
              "Your shirt will be handed out during Social Run for Spain. There is no postal shipping.",
              "Tu camiseta se recogerá durante la Social Run for Spain. No hay envío postal."
            )}
          </p>
          {rec && (
            <p className="mt-4 text-ink/50 text-sm">
              {Number(rec.amount).toLocaleString("da-DK")} DKK
            </p>
          )}
          <Link to="/" className="mt-10 inline-block border border-ink px-6 py-3 text-sm tracking-wide hover:bg-ink hover:text-white transition-colors rounded-full">
            {t("Til forsiden", "Back to home", "Volver al inicio")}
          </Link>
        </>
      ) : status === "loading" ? (
        <p className="text-ink/60">…</p>
      ) : (
        <p className="text-red-600">
          {t("Betalingen kunne ikke bekræftes. Kontakt os venligst.",
             "Payment could not be confirmed. Please contact us.",
             "No se pudo confirmar el pago. Contáctanos, por favor.")}
        </p>
      )}
    </div>
  );
}
